import { useState } from 'react';
import {
  Accordion,
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { Plus } from 'react-feather';
import { CountStepper } from '../../components/CountStepper';
import { listLabel, type ValueCount } from '../../model/derive';
import {
  avoidText,
  BUDDY_MAX,
  BUDDY_MIN,
  CLAMP_MESSAGE,
  combinationConflict,
  isRanged,
  limitText,
  SHOW_VALUES,
  typesConstrained,
  typesMax,
  typesMin,
  valueLimit,
} from '../../model/goals';
import type {
  AvoidCombination,
  Column,
  PerValueLimit,
  SpreadGoal,
} from '../../model/types';
import { SERVER_SUPPORTS } from '../../model/serverSupport';
import { useSetup } from '../../SetupContext';

interface SpreadGoalPanelProps {
  goal: SpreadGoal;
  column: Column;
  values: ValueCount[];
  openSections: string[];
  onOpenSectionsChange: (sections: string[]) => void;
  onNotice: (message: string) => void;
}

/** Values long enough to need a search box when picking a combination. */
const SEARCH_THRESHOLD = 8;

export function SpreadGoalPanel({
  goal,
  column,
  values,
  openSections,
  onOpenSectionsChange,
  onNotice,
}: SpreadGoalPanelProps) {
  const { config, update, readOnly } = useSetup();
  const [showAllValues, setShowAllValues] = useState(false);
  const n = values.length;
  const label = column.label.toLowerCase();
  const tMin = typesMin(goal, n);
  const tMax = typesMax(goal, n);

  const save = (next: SpreadGoal) => {
    update({ type: 'updateGoal', column: goal.column, goal: next });
    onNotice('');
  };
  const setLimit = (value: string, patch: PerValueLimit) =>
    save({
      ...goal,
      perValue: {
        ...goal.perValue,
        [value]: { ...goal.perValue[value], ...patch },
      },
    });

  // --- summaries under each section name ---
  const typesSummary = !typesConstrained(goal, n)
    ? 'Any number of types'
    : tMin > 1 && tMax >= n
      ? `At least ${tMin} types`
      : tMin <= 1
        ? `At most ${tMax} types`
        : `${tMin} to ${tMax} types`;
  const peopleParts = values.flatMap(({ value }) => {
    const r = valueLimit(goal, value);
    if (r.every && isRanged(r))
      return [`${value}: in every group, ${limitText(r)}`];
    if (r.every) return [`${value}: in every group`];
    if (isRanged(r)) return [`${value}: ${limitText(r)}`];
    return [];
  });
  const peopleSummary =
    peopleParts.length === 0
      ? 'No limits set'
      : `${peopleParts.slice(0, 2).join('; ')}${peopleParts.length > 2 ? `; +${peopleParts.length - 2} more` : ''}`;

  const shown = showAllValues ? values : values.slice(0, SHOW_VALUES);

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        Open a section to set how {column.label} spreads out across groups. What
        you've set shows under each section name.
      </Text>
      <Accordion
        multiple
        variant="separated"
        radius="md"
        value={openSections}
        onChange={onOpenSectionsChange}
      >
        <Accordion.Item value="types">
          <Accordion.Control>
            <Text fw={600}>Number of types in each group</Text>
            <Text size="sm" c="dimmed">
              {typesSummary}
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Text size="sm" c="dimmed" mb="md">
              {SERVER_SUPPORTS.typesMax
                ? `How many different types of ${column.label} a group can have.`
                : `The fewest different types of ${column.label} a group can have.`}
            </Text>
            <Stack gap="sm">
              <Group gap="sm">
                <Text w={70} fw={500} size="sm">
                  Fewest
                </Text>
                <CountStepper
                  value={String(tMin)}
                  valueWidth={70}
                  decrementLabel="Fewer types at minimum"
                  incrementLabel="More types at minimum"
                  decrementDisabled={readOnly || tMin <= 1}
                  incrementDisabled={readOnly}
                  onDecrement={() =>
                    save({
                      ...goal,
                      types: { ...goal.types, min: Math.max(1, tMin - 1) },
                    })
                  }
                  onIncrement={() =>
                    tMin + 1 > tMax
                      ? onNotice(CLAMP_MESSAGE)
                      : save({
                          ...goal,
                          types: { ...goal.types, min: tMin + 1 },
                        })
                  }
                />
              </Group>
              {/* Hidden until matching-server reads types.max. */}
              {SERVER_SUPPORTS.typesMax && (
                <Group gap="sm">
                  <Text w={70} fw={500} size="sm">
                    Most
                  </Text>
                  <CountStepper
                    value={tMax >= n ? 'No limit' : String(tMax)}
                    valueWidth={70}
                    decrementLabel="Fewer types at maximum"
                    incrementLabel="More types at maximum"
                    decrementDisabled={readOnly}
                    incrementDisabled={readOnly || tMax >= n}
                    onDecrement={() =>
                      tMax - 1 < tMin
                        ? onNotice(CLAMP_MESSAGE)
                        : save({
                            ...goal,
                            types: { ...goal.types, max: tMax - 1 },
                          })
                    }
                    onIncrement={() =>
                      save({
                        ...goal,
                        types: {
                          ...goal.types,
                          max: tMax + 1 >= n ? null : tMax + 1,
                        },
                      })
                    }
                  />
                </Group>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="buddy">
          <Accordion.Control>
            <Text fw={600}>Buddy mix</Text>
            <Text size="sm" c="dimmed">
              {goal.buddy.enabled
                ? `On: at least ${goal.buddy.minPerType} of each type`
                : 'Off'}
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Switch
              checked={goal.buddy.enabled}
              disabled={readOnly}
              onChange={() =>
                save({
                  ...goal,
                  buddy: { ...goal.buddy, enabled: !goal.buddy.enabled },
                })
              }
              label="Everyone has at least one other person of their same type"
              description="Nobody is the only one of their type in a group."
            />
            {goal.buddy.enabled && (
              <Group gap="sm" mt="md">
                <Text fw={500} size="sm">
                  Every group has at least
                </Text>
                <CountStepper
                  value={String(goal.buddy.minPerType)}
                  valueWidth={24}
                  decrementLabel="Fewer of each type"
                  incrementLabel="More of each type"
                  decrementDisabled={
                    readOnly || goal.buddy.minPerType <= BUDDY_MIN
                  }
                  incrementDisabled={
                    readOnly || goal.buddy.minPerType >= BUDDY_MAX
                  }
                  onDecrement={() =>
                    save({
                      ...goal,
                      buddy: {
                        ...goal.buddy,
                        minPerType: goal.buddy.minPerType - 1,
                      },
                    })
                  }
                  onIncrement={() =>
                    save({
                      ...goal,
                      buddy: {
                        ...goal.buddy,
                        minPerType: goal.buddy.minPerType + 1,
                      },
                    })
                  }
                />
                <Text fw={500} size="sm">
                  of each type
                </Text>
              </Group>
            )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="limits">
          <Accordion.Control>
            <Text fw={600}>People of each type in each group</Text>
            <Text size="sm" c="dimmed">
              {peopleSummary}
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Text size="sm" c="dimmed" mb="sm">
              Define mixing goals for specific representation of {label} in each
              group.
            </Text>
            {goal.buddy.enabled && (
              <Paper bg="gray.0" p="xs" radius="md" mb="sm">
                <Text size="sm" c="dimmed">
                  With Buddy mix, any type in a group has at least{' '}
                  {goal.buddy.minPerType} people, so Fewest can't go below{' '}
                  {goal.buddy.minPerType}.
                </Text>
              </Paper>
            )}
            <Table withTableBorder verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th>
                  <Table.Th ta="center" w={110}>
                    In every group
                  </Table.Th>
                  <Table.Th ta="center" w={130}>
                    Fewest
                  </Table.Th>
                  <Table.Th ta="center" w={150}>
                    Most
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {shown.map(({ value, count }) => {
                  const r = valueLimit(goal, value);
                  return (
                    <Table.Tr key={value}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {value}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {count} in the cohort
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group justify="center">
                          <Checkbox
                            aria-label={`${value} must be in every group`}
                            checked={r.every}
                            disabled={readOnly}
                            onChange={() =>
                              setLimit(value, { inEveryGroup: !r.every })
                            }
                          />
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <CountStepper
                          size="sm"
                          value={String(r.min)}
                          valueWidth={24}
                          decrementLabel={`Decrease fewest ${value}`}
                          incrementLabel={`Increase fewest ${value}`}
                          decrementDisabled={readOnly || r.min <= r.floor}
                          incrementDisabled={readOnly}
                          onDecrement={() =>
                            setLimit(value, {
                              min: Math.max(r.floor, r.min - 1),
                            })
                          }
                          onIncrement={() =>
                            r.min + 1 > r.max
                              ? onNotice(CLAMP_MESSAGE)
                              : setLimit(value, { min: r.min + 1 })
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <CountStepper
                          size="sm"
                          value={
                            r.max === Infinity ? 'No limit' : String(r.max)
                          }
                          valueWidth={56}
                          decrementLabel={`Decrease most ${value}`}
                          incrementLabel={`Increase most ${value}`}
                          decrementDisabled={readOnly}
                          incrementDisabled={readOnly || r.max === Infinity}
                          onDecrement={() => {
                            const next =
                              r.max === Infinity
                                ? config.groupSize.max
                                : r.max - 1;
                            if (next < r.min) onNotice(CLAMP_MESSAGE);
                            else setLimit(value, { max: next });
                          }}
                          onIncrement={() =>
                            setLimit(value, {
                              max:
                                r.max + 1 > config.groupSize.max
                                  ? undefined
                                  : r.max + 1,
                            })
                          }
                        />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            {values.length > SHOW_VALUES && (
              <Anchor
                component="button"
                type="button"
                size="sm"
                mt="xs"
                onClick={() => setShowAllValues(v => !v)}
              >
                {showAllValues
                  ? 'Show fewer values'
                  : `Show all ${values.length} values`}
              </Anchor>
            )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="avoid">
          <Accordion.Control>
            <Text fw={600}>Combinations to avoid</Text>
            <Text size="sm" c="dimmed">
              {goal.avoid.length === 0
                ? 'None'
                : `${goal.avoid.length} ${goal.avoid.length === 1 ? 'combination' : 'combinations'}`}
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <CombinationsSection
              goal={goal}
              values={values}
              onSave={save}
              onNotice={onNotice}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}

function CombinationsSection({
  goal,
  values,
  onSave,
  onNotice,
}: {
  goal: SpreadGoal;
  values: ValueCount[];
  onSave: (goal: SpreadGoal) => void;
  onNotice: (message: string) => void;
}) {
  const { readOnly } = useSetup();
  const [adding, setAdding] = useState(false);
  const [match, setMatch] = useState<AvoidCombination['match']>('exact');
  const [picked, setPicked] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const q = query.trim().toLowerCase();
  let pool = values
    .map(v => v.value)
    .filter(v => !q || v.toLowerCase().includes(q));
  const hasMore = !q && !showAll && pool.length > SHOW_VALUES;
  if (hasMore) pool = pool.slice(0, SHOW_VALUES);

  const reset = () => {
    setAdding(false);
    setPicked([]);
    setQuery('');
    onNotice('');
  };

  return (
    <Stack gap="md">
      <Box>
        <Text size="sm" c="dimmed">
          Exact combination: avoids forming groups made up of exactly these
          types.
        </Text>
        <Text size="sm" c="dimmed" mt={6}>
          All of these together: avoids forming groups that have every one of
          the selected types, whatever other types are in them.
        </Text>
      </Box>
      {goal.avoid.map((c, i) => {
        const conflict = combinationConflict(goal, values, c);
        return (
          <Paper
            key={`${c.match}-${c.values.join('|')}`}
            withBorder
            radius="md"
            p="sm"
            style={
              conflict
                ? { borderColor: 'var(--mantine-color-dangerRed-5)' }
                : undefined
            }
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Box style={{ minWidth: 0 }}>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={6}>
                  Avoid ·{' '}
                  {c.match === 'exact'
                    ? 'Exact combination'
                    : 'All of these together'}
                </Text>
                <Group gap={6} mb={6}>
                  {c.values.map(v => (
                    <Badge key={v} color="gray" variant="light" tt="none">
                      {v}
                    </Badge>
                  ))}
                </Group>
                <Text size="sm" c="dimmed">
                  {avoidText(c)}
                </Text>
                {conflict && (
                  <Text size="sm" fw={600} c="dangerRed.9" mt={4}>
                    {conflict}
                  </Text>
                )}
              </Box>
              {!readOnly && (
                <Button
                  variant="default"
                  size="xs"
                  aria-label="Remove combination"
                  onClick={() =>
                    onSave({
                      ...goal,
                      avoid: goal.avoid.filter((_, j) => j !== i),
                    })
                  }
                >
                  Remove
                </Button>
              )}
            </Group>
          </Paper>
        );
      })}
      {!readOnly && !adding && (
        <Button
          variant="outline"
          leftSection={<Plus size={16} />}
          style={{ alignSelf: 'flex-start', borderStyle: 'dashed' }}
          onClick={() => setAdding(true)}
        >
          Add combination
        </Button>
      )}
      {adding && (
        <Paper
          p="md"
          radius="md"
          style={{ border: '1px dashed var(--mantine-color-gray-5)' }}
        >
          <Group gap="sm" mb="sm">
            <Select
              aria-label="Combination type"
              w={200}
              allowDeselect={false}
              value={match}
              onChange={v =>
                setMatch((v as AvoidCombination['match']) ?? 'exact')
              }
              data={[
                { value: 'exact', label: 'Exact combination' },
                { value: 'any', label: 'All of these together' },
              ]}
            />
            <Text size="sm" c="dimmed">
              to avoid in the same group
            </Text>
          </Group>
          {values.length > SEARCH_THRESHOLD && (
            <TextInput
              aria-label="Search types"
              placeholder="Search types"
              mb="sm"
              value={query}
              onChange={e => setQuery(e.currentTarget.value)}
            />
          )}
          <Chip.Group multiple value={picked} onChange={setPicked}>
            <Group gap="xs">
              {pool.map(v => (
                <Chip key={v} value={v}>
                  {v}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
          {hasMore && (
            <Anchor
              component="button"
              type="button"
              size="sm"
              mt="xs"
              onClick={() => setShowAll(true)}
            >
              Show all {values.length} values
            </Anchor>
          )}
          <Text size="sm" c="dimmed" mt="sm">
            {picked.length === 0
              ? 'Pick two or more types.'
              : `Selected: ${listLabel(picked)}.`}
          </Text>
          <Group gap="sm" mt="sm">
            <Button
              onClick={() => {
                if (picked.length < 2) {
                  onNotice('Pick at least two types for a combination.');
                  return;
                }
                onSave({
                  ...goal,
                  avoid: [...goal.avoid, { match, values: picked }],
                });
                reset();
              }}
            >
              Save combination
            </Button>
            <Button variant="default" onClick={reset}>
              Cancel
            </Button>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
